import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import type { IssueDetail, Severity, Status } from '../api/types';
import { SeverityBadge, StatusBadge, Tag } from '../components/Badge';
import { STORAGE_API } from '../config';

const SEVERITIES: Severity[] = ['critical', 'major', 'minor', 'cosmetic'];
const STATUSES: Status[] = [
  'open',
  'in_progress',
  'fixed',
  'deferred',
  'closed',
  'duplicate',
  'wontfix',
];

export function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const issueId = Number(id);
  const [issue, setIssue] = useState<IssueDetail | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<IssueDetail>>({});
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    setError('');
    api
      .get(issueId)
      .then((d) => {
        setIssue(d);
        setDraft({});
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Issue konnte nicht geladen werden'));
  }, [issueId]);

  if (!issue && !error) {
    return (
      <div className="empty-state">
        <span className="spinner" /> &nbsp; lädt…
      </div>
    );
  }
  if (error || !issue) {
    return <div className="error">{error || 'Issue nicht gefunden'}</div>;
  }

  async function saveEdits() {
    if (!Object.keys(draft).length) {
      setEditing(false);
      return;
    }
    try {
      await api.patch(issueId, draft);
      const fresh = await api.get(issueId);
      setIssue(fresh);
      setDraft({});
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update fehlgeschlagen');
    }
  }

  async function submitComment() {
    if (!comment.trim() || posting) return;
    setPosting(true);
    try {
      await api.comment(issueId, comment.trim());
      const fresh = await api.get(issueId);
      setIssue(fresh);
      setComment('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kommentar konnte nicht gespeichert werden');
    } finally {
      setPosting(false);
    }
  }

  const current = { ...issue, ...draft } as IssueDetail;

  return (
    <>
      <div className="page-header">
        <Link to="/list" className="breadcrumb">← liste</Link>
      </div>

      <div className="card">
        <div className="detail-header">
          <div style={{ flex: 1 }}>
            <div className="id-tag">ISSUE · #{String(issue.id).padStart(3, '0')}</div>
            {editing ? (
              <input
                value={current.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                style={{ fontSize: 18, fontWeight: 600, padding: '8px 10px' }}
              />
            ) : (
              <h2>{current.title}</h2>
            )}
          </div>
          <button onClick={() => (editing ? saveEdits() : setEditing(true))}>
            {editing ? 'speichern' : 'bearbeiten'}
          </button>
        </div>

        <div className="detail-meta-row">
          {editing ? (
            <>
              <select
                value={current.severity}
                onChange={(e) => setDraft({ ...draft, severity: e.target.value as Severity })}
                style={{ width: 'auto' }}
              >
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={current.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value as Status })}
                style={{ width: 'auto' }}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input
                placeholder="component"
                value={current.component || ''}
                onChange={(e) => setDraft({ ...draft, component: e.target.value || null })}
                style={{ width: 160 }}
              />
            </>
          ) : (
            <>
              <SeverityBadge severity={current.severity} />
              <StatusBadge status={current.status} />
              {current.component && <span className="tag">{current.component}</span>}
            </>
          )}
        </div>

        {current.tags.length > 0 && (
          <div className="tag-list" style={{ marginBottom: 14 }}>
            {current.tags.map((t) => <Tag key={t}>{t}</Tag>)}
          </div>
        )}

        <div className="description-block">{current.description}</div>

        <dl className="field-grid">
          <dt>Reporter</dt><dd>{current.reporter} <span className="muted">({current.reporter_type})</span></dd>
          {current.assignee && (<><dt>Assignee</dt><dd>{current.assignee}</dd></>)}
          <dt>Tenant</dt><dd>{current.tenant_id}</dd>
          {current.file_pointer && (<><dt>File</dt><dd>{current.file_pointer}</dd></>)}
          {current.resolution_commit && (<><dt>Resolution</dt><dd>{current.resolution_commit}</dd></>)}
          {current.resolution_summary && (
            <><dt>Summary</dt><dd className="prose">{current.resolution_summary}</dd></>
          )}
          {current.fixed_at && (
            <><dt>Fixed</dt><dd>{new Date(current.fixed_at).toLocaleString('de-DE')} <span className="muted">by {current.fixed_by}</span></dd></>
          )}
          <dt>Created</dt><dd>{new Date(current.created_at).toLocaleString('de-DE')}</dd>
        </dl>

        {issue.attachments.length > 0 && (
          <div className="attachments">
            {issue.attachments.map((a) => (
              <a
                key={a.id}
                href={`${STORAGE_API}/storage/media/${a.storage_id}`}
                target="_blank"
                rel="noreferrer"
                className="thumb"
              >
                <img
                  src={`${STORAGE_API}/storage/media/${a.storage_id}`}
                  alt={a.caption || a.role}
                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                />
                <div className="thumb-caption">{a.role} · {a.storage_id}</div>
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="page-header" style={{ marginBottom: 12 }}>
          <h2>Comments</h2>
          <span className="breadcrumb">{issue.comments.length}</span>
        </div>
        {issue.comments.map((c) => (
          <div key={c.id} className="comment">
            <div className="comment-meta">
              <span>{c.author}</span>
              <span>·</span>
              <span>{new Date(c.created_at).toLocaleString('de-DE')}</span>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{c.body}</div>
          </div>
        ))}
        <div style={{ marginTop: 14 }}>
          <textarea
            placeholder="Kommentar hinzufügen…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={posting}
          />
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button className="primary" onClick={submitComment} disabled={!comment.trim() || posting}>
              {posting ? 'sende…' : 'senden'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
